/**
 * @file        QueuePage.jsx
 * @module      modules/kyb/manager/console
 * @description Review queue — every sign-up, oldest deadline first. Pulse
 *              strip, tabs, filters, bulk assign and the cases table, as in
 *              the shared HTML design; the summary cards and "Time period"
 *              follow the updated design. Tab, period, search and filters live
 *              in the URL (?tab=&period=&q=&type=&assignee=&source=&page=) so
 *              Back from a case returns to the same list.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { kybManagerApi } from "../api/kybManagerApi";
import { Chip, Sla, TABS, fmtWhen, initials, useToast } from "./ui";

const PERIODS = [
  ["", "All time"],
  ["today", "Today"],
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
  ["90d", "Last 90 days"],
];

export default function QueuePage({ me: viewer, onCounts }) {
  // KYB executives can't assign cases to anyone else: no bulk select / assign.
  const canBulk = viewer?.role !== "kyb_executive";
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "review";
  const filters = {
    q: params.get("q") || "",
    type: params.get("type") || "",
    assignee: params.get("assignee") || "",
    source: params.get("source") || "",
    period: params.get("period") || "",
  };
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const [q, setQ] = useState(filters.q);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [bulkTo, setBulkTo] = useState("");
  const [reload, setReload] = useState(0);

  const set = useCallback(
    (changes) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      if (!("page" in changes)) next.delete("page");
      setParams(next);
      setSel(new Set());
    },
    [params, setParams],
  );

  const key = `${tab}|${filters.q}|${filters.type}|${filters.assignee}|${filters.source}|${filters.period}|${page}|${reload}`;
  useEffect(() => {
    let cancelled = false;
    kybManagerApi
      .cases({ tab, ...filters, page, limit: 25 })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
        onCounts && onCounts(d.counts);
      })
      .catch((e) => !cancelled && setError(e.message || "Couldn't load the queue."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Debounced search box.
  useEffect(() => {
    if (q === filters.q) return undefined;
    const t = setTimeout(() => set({ q }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const team = data?.team || [];
  const me = data?.me;

  const assign = async (ids, to) => {
    try {
      await kybManagerApi.assign(ids, to);
      const name = team.find((t) => t.id === to)?.name;
      toast(ids.length > 1 ? `${ids.length} cases assigned to ${name}` : to === me?.id ? "Assigned to you" : `Assigned to ${name}`);
      setSel(new Set());
      setReload((n) => n + 1);
    } catch (e) {
      toast(e.message || "Couldn't assign.");
    }
  };

  const p = data?.pulse;
  const allSelected = rows.length > 0 && rows.every((r) => sel.has(r.id));
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Review queue</h1>
          <p>Every sign-up, oldest deadline first. Cases needing review are due one working day after submission.</p>
        </div>
        <label className="period">
          <span>Time period</span>
          <select value={filters.period} onChange={(e) => set({ period: e.target.value })} aria-label="Time period">
            {PERIODS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="banner bad" style={{ margin: "0 0 16px" }}>
          <div>
            <b>Couldn't load the queue</b>
            {error}
          </div>
          <button className="btn small ghost" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : null}

      <div className="stats">
        <button className="stat" onClick={() => set({ tab: "progress" })}>
          <span className="lbl">KYC in process</span>
          <b>{p?.progress ?? 0}</b>
          <small>{p?.abandoned ?? 0} abandoned · resume where left</small>
        </button>
        <button className="stat" onClick={() => set({ tab: "auto" })}>
          <span className="lbl">Auto-approved</span>
          <b>{p?.auto ?? 0}</b>
          <small>Booking enabled</small>
        </button>
        <div className="stat split">
          <button className="main" onClick={() => set({ tab: "review" })}>
            <span className="lbl">Needs action</span>
            <b className="amber">{p?.review ?? 0}</b>
            <small>With KYC team</small>
          </button>
          <button className="part ok" onClick={() => set({ tab: "review" })}>
            <span className="lbl"><i />Due &gt; 4h</span>
            <b>{p?.later ?? 0}</b>
            <small>On track</small>
          </button>
          <button className="part warn" onClick={() => set({ tab: "review" })}>
            <span className="lbl"><i />Due &lt; 4h</span>
            <b>{p?.soon ?? 0}</b>
            <small>At risk</small>
          </button>
          <button className={`part bad${p?.overdue ? " hot" : ""}`} onClick={() => set({ tab: "review" })}>
            <span className="lbl"><i />Overdue</span>
            <b>{p?.overdue ?? 0}</b>
            <small>Past SLA</small>
          </button>
        </div>
        <button className="stat" onClick={() => set({ tab: "awaiting" })}>
          <span className="lbl">Customer action pending</span>
          <b>{p?.awaiting ?? 0}</b>
          <small>{p?.docsPastDue ?? 0} documents past due</small>
        </button>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => set({ tab: k })}>
            {label}
            <span className="c">{data?.counts?.[k] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="filters">
        <input type="search" placeholder="Search business, PAN, case ID or person" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <select value={filters.type} onChange={(e) => set({ type: e.target.value })} aria-label="Business type">
          <option value="">All business types</option>
          {(data?.types || []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select value={filters.assignee} onChange={(e) => set({ assignee: e.target.value })} aria-label="Assignee">
          <option value="">Anyone</option>
          <option value="none">Unassigned</option>
          {team.map((t) => (
            <option key={t.id} value={t.id}>
              {t.id === me?.id ? `${t.name} (you)` : t.name}
            </option>
          ))}
        </select>
        <select value={filters.source} onChange={(e) => set({ source: e.target.value })} aria-label="Source">
          <option value="">All sources</option>
          <option value="api">Automatic checks</option>
          <option value="fallback">API fallback (documents)</option>
        </select>
        {canBulk && sel.size ? (
          <span className="bulk">
            {sel.size} selected
            <select value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} aria-label="Assign selected to">
              <option value="">Assign to…</option>
              {team.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <button className="btn small primary" disabled={!bulkTo} onClick={() => assign([...sel], bulkTo)}>
              Assign
            </button>
          </span>
        ) : null}
      </div>

      <div className="tablewrap">
        {!data && !error ? (
          <div className="loading">Loading…</div>
        ) : rows.length ? (
          <table className="q">
            <thead>
              <tr>
                {canBulk ? (
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allSelected}
                      onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                    />
                  </th>
                ) : null}
                <th>Business</th>
                <th>Type</th>
                <th>Status</th>
                <th>Why it's here</th>
                <th>SLA</th>
                <th>Assignee</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/kyb-manager/case/${c.id}`)}>
                  {canBulk ? (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${c.business.name || c.caseNo}`}
                        checked={sel.has(c.id)}
                        onChange={(e) =>
                          setSel((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(c.id);
                            else n.delete(c.id);
                            return n;
                          })
                        }
                      />
                    </td>
                  ) : null}
                  <td className="biz">
                    <b>{c.business.name || "Business not entered yet"}</b>
                    <small>{[c.caseNo, c.business.pan, c.person.name].filter(Boolean).join(" · ")}</small>
                  </td>
                  <td>{c.type}</td>
                  <td>
                    <Chip status={c.status} />
                  </td>
                  <td>
                    <div className="reasons">
                      {c.source === "fallback" ? <span className="tag fallback">API fallback</span> : null}
                      {c.reasons.length ? c.reasons.map((r) => <span key={r} className="tag">{r}</span>) : <span className="tag">All checks passed</span>}
                    </div>
                  </td>
                  <td>
                    <Sla status={c.status} dueAt={c.slaDueAt} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {c.assignee ? (
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                        <span className="avatar grey">{initials(c.assignee.name)}</span>
                        {c.assignee.name}
                      </span>
                    ) : c.status === "review" || c.status === "awaiting" ? (
                      <button className="btn small ghost" onClick={() => assign([c.id], me?.id)}>
                        Assign to me
                      </button>
                    ) : (
                      <span style={{ color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap", color: "var(--muted)" }}>{fmtWhen(c.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">
            <b style={{ color: "var(--text)" }}>Nothing here</b>
            <br />
            No sign-ups match these filters. Clear the search or pick another tab.
          </div>
        )}
      </div>

      {pages > 1 ? (
        <div className="pager">
          <button className="btn small ghost" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}>
            Previous
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button className="btn small ghost" disabled={page >= pages} onClick={() => set({ page: String(page + 1) })}>
            Next
          </button>
        </div>
      ) : null}
    </>
  );
}
